import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Stack,
  Grid,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StadiumIcon from '@mui/icons-material/Stadium';
import InfoIcon from '@mui/icons-material/Info';
import config from '../config';

const MATCH_TYPES = ['Group stage', 'Round of 16', 'Quarter Final', 'Semi Final', 'Final'];

const AdminMatches = () => {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [homeTeamId, setHomeTeamId] = useState('');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [time, setTime] = useState('');
  const [field, setField] = useState('');
  const [formError, setFormError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterTeamId, setFilterTeamId] = useState('');
  const [homeTeamScore, setHomeTeamScore] = useState('');
  const [awayTeamScore, setAwayTeamScore] = useState('');
  const [matchType, setMatchType] = useState('');
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [homeTeamScorers, setHomeTeamScorers] = useState([]);
  const [awayTeamScorers, setAwayTeamScorers] = useState([]);
  const [scorerDialogError, setScorerDialogError] = useState('');
  const [isFinished, setIsFinished] = useState(false);
  const [ongoing, setOngoing] = useState(false);
  const token = localStorage.getItem('token');
  const [filterMatchType, setFilterMatchType] = useState('');
  const [scoreWarningOpen, setScoreWarningOpen] = useState(false);
  const [selectedMatchForWarning, setSelectedMatchForWarning] = useState(null);

  const fetchMatches = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await fetch(`${config.apiBaseUrl}/matches?year=${currentYear}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching matches', severity: 'error' });
    }
  }, [token]);

  const fetchTeams = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await fetch(`${config.apiBaseUrl}/teams?year=${currentYear}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching teams', severity: 'error' });
    }
  }, [token]);

  useEffect(() => {
    fetchMatches();
    fetchTeams();
  }, [fetchMatches, fetchTeams]);

  // Filtering
  const filteredMatches = matches
    .filter(
      m => !filterTeamId || m.home_team_id === filterTeamId || m.away_team_id === filterTeamId
    )
    .filter(m => !filterMatchType || m.type === filterMatchType);

  // Validation
  const validateMatchForm = () => {
    if (!homeTeamId || !awayTeamId || !time || !field) {
      setFormError('All fields are required.');
      return false;
    }
    if (homeTeamId === awayTeamId) {
      setFormError('Home and Away teams must be different.');
      return false;
    }
    setFormError('');
    return true;
  };

  const handleMatchSubmit = async () => {
    if (!validateMatchForm()) return;
    try {
      const response = await fetch(`${config.apiBaseUrl}/matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          home_team_id: homeTeamId,
          away_team_id: awayTeamId,
          time: convertToFinnishTime(time),
          field: field,
          type: matchType,
        }),
      });
      if (response.ok) {
        setMatchDialogOpen(false);
        setHomeTeamId('');
        setAwayTeamId('');
        setTime('');
        setField('');
        fetchMatches();
        setSnackbar({ open: true, message: 'Match added successfully!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Failed to add match', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error creating match', severity: 'error' });
    }
  };

  // Fetch score for a match
  const fetchScoreForMatch = async matchId => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/scores`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const scores = await response.json();
        const score = scores.find(s => s.match_id === matchId);
        return score;
      }
    } catch (error) {
      // ignore
    }
    return null;
  };

  // Fetch scorers for a match
  const fetchScorersForMatch = async matchId => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/scorers/match/${matchId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const scorers = await response.json();
        return scorers;
      }
    } catch (error) {
      // Error fetching scorers for match
    }
    return [];
  };

  // Edit match handlers
  const handleEditClick = async match => {
    setSelectedMatch(match);
    setHomeTeamId(match.home_team_id);
    setAwayTeamId(match.away_team_id);
    setTime(match.time ? convertFromFinnishTime(match.time) : '');
    setField(match.field);
    setMatchType(match.type || '');
    setIsFinished(match.isFinished || false);
    setOngoing(match.ongoing || false);
    setEditDialogOpen(true);
    setFormError('');
    // Fetch score for this match
    const score = await fetchScoreForMatch(match.id);
    setHomeTeamScore(score ? score.home_team_score : '');
    setAwayTeamScore(score ? score.away_team_score : '');
    setEditingScoreId(score ? score.id : null);

    // Initialize scorers arrays based on scores
    setHomeTeamScorers(
      Array(Number(score?.home_team_score || 0)).fill({ player_id: '', score_minute: '' })
    );
    setAwayTeamScorers(
      Array(Number(score?.away_team_score || 0)).fill({ player_id: '', score_minute: '' })
    );
  };

  // Track which score is being edited
  const [editingScoreId, setEditingScoreId] = useState(null);

  const handleEditSubmit = async () => {
    if (!validateMatchForm()) return;
    if (!selectedMatch) return;
    try {
      // Check if teams are being changed
      const teamsChanged =
        homeTeamId !== selectedMatch.home_team_id || awayTeamId !== selectedMatch.away_team_id;

      // If teams are changed, fetch existing scorers and try to migrate them
      let existingScorers = [];
      let migratableScorers = [];
      if (teamsChanged) {
        existingScorers = await fetchScorersForMatch(selectedMatch.id);

        if (existingScorers.length > 0) {
          // Get player lists for new teams
          const newHomeTeam = teams.find(t => t.id === parseInt(homeTeamId));
          const newAwayTeam = teams.find(t => t.id === parseInt(awayTeamId));

          // Try to migrate existing scorers to new teams
          migratableScorers = existingScorers.filter(scorer => {
            // Check if this player exists in either of the new teams
            const playerInNewHomeTeam = newHomeTeam?.players?.some(p => p.id === scorer.player_id);
            const playerInNewAwayTeam = newAwayTeam?.players?.some(p => p.id === scorer.player_id);
            return playerInNewHomeTeam || playerInNewAwayTeam;
          });
        }
      }

      // Update match info
      const response = await fetch(`${config.apiBaseUrl}/matches/${selectedMatch.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          home_team_id: homeTeamId,
          away_team_id: awayTeamId,
          time: convertToFinnishTime(time),
          field: field,
          type: matchType,
          isFinished: isFinished,
          ongoing: ongoing,
        }),
      });

      // Only handle scorer preservation if teams changed
      if (teamsChanged && existingScorers.length > 0) {
        // First, delete all existing scorers
        await fetch(`${config.apiBaseUrl}/scorers/match/${selectedMatch.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        // Re-create the scorers that can be migrated
        if (migratableScorers.length > 0) {
          for (const scorer of migratableScorers) {
            await fetch(`${config.apiBaseUrl}/scorers`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                match_id: selectedMatch.id,
                player_id: scorer.player_id,
                score_minute: scorer.score_minute,
              }),
            });
          }
        }
      }

      // Note: Scores and finish status are now handled exclusively through the score dialog (🏆)
      if (response.ok) {
        setEditDialogOpen(false);
        setSelectedMatch(null);
        setHomeTeamId('');
        setAwayTeamId('');
        setTime('');
        setField('');
        fetchMatches();

        // Create appropriate success message
        let successMessage = 'Match updated successfully!';
        if (teamsChanged && existingScorers.length > 0) {
          const migratedCount = migratableScorers.length;
          const lostCount = existingScorers.length - migratedCount;

          if (migratedCount > 0 && lostCount === 0) {
            successMessage = `Match updated successfully! All ${migratedCount} scorers were preserved.`;
          } else if (migratedCount > 0 && lostCount > 0) {
            successMessage = `Match updated successfully! ${migratedCount} scorers preserved, ${lostCount} could not be migrated to new teams.`;
          } else if (lostCount > 0) {
            successMessage = `Match updated successfully! ${lostCount} scorers were removed because they don't belong to the new teams.`;
          }
        }

        setSnackbar({ open: true, message: successMessage, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Failed to update match', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error editing match', severity: 'error' });
    }
  };

  // Delete match handlers
  const handleDeleteClick = match => {
    setSelectedMatch(match);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMatch) return;
    try {
      const response = await fetch(`${config.apiBaseUrl}/matches/${selectedMatch.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        setDeleteDialogOpen(false);
        setSelectedMatch(null);
        fetchMatches();
        setSnackbar({ open: true, message: 'Match deleted successfully!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Failed to delete match', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error deleting match', severity: 'error' });
    }
  };

  const getTeamName = teamId => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.team_name : 'Unknown Team';
  };

  const formatMatchTime = time => {
    if (!time) return 'TBD';
    const date = new Date(time);
    return date.toLocaleDateString('fi-FI', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Helsinki',
    });
  };

  // Helper function to convert datetime-local input to Finnish timezone
  const convertToFinnishTime = datetimeLocal => {
    if (!datetimeLocal) return '';
    // Create date from the datetime-local input (which is in user's local time)
    const localDate = new Date(datetimeLocal);
    // Convert to Finnish timezone
    const finnishTime = localDate.toLocaleString('sv-SE', {
      timeZone: 'Europe/Helsinki',
    });
    // Convert back to ISO format for API
    return new Date(finnishTime).toISOString();
  };

  // Helper function to convert Finnish time to datetime-local format
  const convertFromFinnishTime = isoString => {
    if (!isoString) return '';
    const date = new Date(isoString);
    // Convert to Finnish timezone and format for datetime-local input
    const finnishDate = new Date(
      date.toLocaleString('en-US', {
        timeZone: 'Europe/Helsinki',
      })
    );
    return finnishDate.toISOString().slice(0, 16);
  };

  const renderMatchCard = match => {
    const homeTeamName = getTeamName(match.home_team_id);
    const awayTeamName = getTeamName(match.away_team_id);
    const score = match.Score;

    return (
      <Grid item xs={12} sm={6} md={4} key={match.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#FFFFFF',
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
            },
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            {/* Match Status */}
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              {match.type && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label={match.type}
                    size="small"
                    sx={{ bgcolor: '#DAA520', color: 'white' }}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {match.ongoing && (
                  <Chip
                    label="Ongoing"
                    size="small"
                    sx={{
                      bgcolor: '#2196F3',
                      color: 'white',
                      mr: 1,
                      '& .MuiChip-label': {
                        fontWeight: 'bold',
                      },
                    }}
                  />
                )}
                {match.isFinished && (
                  <Chip
                    label="Finished"
                    size="small"
                    sx={{
                      bgcolor: '#4CAF50',
                      color: 'white',
                      mr: 1,
                      '& .MuiChip-label': {
                        fontWeight: 'bold',
                      },
                    }}
                  />
                )}
                {!match.isFinished && !match.ongoing && (
                  <Chip
                    label="Upcoming"
                    size="small"
                    sx={{
                      bgcolor: '#DAA520',
                      color: 'white',
                      mr: 1,
                      '& .MuiChip-label': {
                        fontWeight: 'bold',
                      },
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Teams and Score */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#000000', textAlign: 'center', mb: 1 }}>
                {homeTeamName}
                {score && (
                  <Typography component="span" sx={{ mx: 2, color: '#DAA520', fontWeight: 'bold' }}>
                    {score.home_team_score} - {score.away_team_score}
                  </Typography>
                )}
                {!score && (
                  <Typography component="span" sx={{ mx: 2 }}>
                    vs
                  </Typography>
                )}
                {awayTeamName}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Match Details */}
            <Stack spacing={2}>
              {/* Time */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon sx={{ color: '#DAA520', mr: 1 }} />
                <Typography variant="body2">{formatMatchTime(match.time)}</Typography>
              </Box>

              {/* Field */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StadiumIcon sx={{ color: '#DAA520', mr: 1 }} />
                <Typography variant="body2">Field: {match.field}</Typography>
              </Box>

              {/* Scorers */}
              {score && score.home_team_score + score.away_team_score > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SportsSoccerIcon sx={{ color: '#DAA520', mr: 1 }} />
                    <Typography variant="subtitle2" sx={{ color: '#DAA520' }}>
                      Scorers
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {match.Scorers &&
                      match.Scorers.map((scorer, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          • {scorer.player_name} ({scorer.score_minute}')
                        </Typography>
                      ))}
                  </Stack>
                </Box>
              )}
            </Stack>

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <IconButton
                onClick={() => handleEditClick(match)}
                sx={{ color: '#DAA520', mr: 1 }}
                size="small"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => handleUpdateScore(match)}
                sx={{ color: '#DAA520', mr: 1 }}
                size="small"
              >
                <span style={{ fontSize: '1.2rem' }}>🏆</span>
              </IconButton>
              <IconButton
                onClick={() => handleDeleteClick(match)}
                sx={{ color: '#DAA520' }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const handleUpdateScore = async match => {
    if (!match.ongoing) {
      setSelectedMatchForWarning(match);
      setScoreWarningOpen(true);
      return;
    }

    setSelectedMatch(match);

    const homeTeam = teams.find(t => t.id === match.home_team_id);
    const awayTeam = teams.find(t => t.id === match.away_team_id);

    const score = await fetchScoreForMatch(match.id);
    const scorers = await fetchScorersForMatch(match.id);

    setHomeTeamScore(score ? score.home_team_score : '');
    setAwayTeamScore(score ? score.away_team_score : '');
    setEditingScoreId(score ? score.id : null);

    // Group scorers by team
    const homeScorers = scorers.filter(scorer =>
      homeTeam?.players?.some(player => player.id === scorer.player_id)
    );
    const awayScorers = scorers.filter(scorer =>
      awayTeam?.players?.some(player => player.id === scorer.player_id)
    );

    // Initialize scorers arrays with existing data
    setHomeTeamScorers(
      Array(Number(score?.home_team_score || 0))
        .fill(null)
        .map((_, index) => ({
          player_id: String(homeScorers[index]?.player_id || ''),
          score_minute: homeScorers[index]?.score_minute || '',
        }))
    );

    setAwayTeamScorers(
      Array(Number(score?.away_team_score || 0))
        .fill(null)
        .map((_, index) => ({
          player_id: String(awayScorers[index]?.player_id || ''),
          score_minute: awayScorers[index]?.score_minute || '',
        }))
    );

    setScoreDialogOpen(true);
  };

  const handleScoreChange = (e, team) => {
    const newValue = e.target.value === '' ? '' : Number(e.target.value);

    if (team === 'home') {
      // Ensure new value is not less than current number of scorers
      const minValue = homeTeamScorers.length;
      if (newValue !== '' && Number(newValue) < minValue) {
        setSnackbar({
          open: true,
          message: `Score cannot be less than ${minValue}`,
          severity: 'error',
        });
        return;
      }

      setHomeTeamScore(newValue);

      // Only update scorers if we have a valid number greater than current
      if (newValue !== '' && Number(newValue) > minValue) {
        const value = Number(newValue);
        const oldValue = homeTeamScorers.length;
        const existingScorers = [...homeTeamScorers];

        // Add new empty slots at the beginning while preserving existing data
        const additionalScorers = Array(value - oldValue)
          .fill(null)
          .map(() => ({ player_id: '', score_minute: '' }));
        const newScorers = [...additionalScorers, ...existingScorers];

        setHomeTeamScorers(newScorers);
      }
    } else {
      // Ensure new value is not less than current number of scorers
      const minValue = awayTeamScorers.length;
      if (newValue !== '' && Number(newValue) < minValue) {
        setSnackbar({
          open: true,
          message: `Score cannot be less than ${minValue}`,
          severity: 'error',
        });
        return;
      }

      setAwayTeamScore(newValue);

      // Only update scorers if we have a valid number greater than current
      if (newValue !== '' && Number(newValue) > minValue) {
        const value = Number(newValue);
        const oldValue = awayTeamScorers.length;
        const existingScorers = [...awayTeamScorers];

        // Add new empty slots at the beginning while preserving existing data
        const additionalScorers = Array(value - oldValue)
          .fill(null)
          .map(() => ({ player_id: '', score_minute: '' }));
        const newScorers = [...additionalScorers, ...existingScorers];

        setAwayTeamScorers(newScorers);
      }
    }
  };

  const handleScorerChange = (index, field, value, team) => {
    // Ensure value is not undefined
    const validValue = value || '';

    if (team === 'home') {
      const newScorers = [...homeTeamScorers];
      newScorers[index] = { ...newScorers[index], [field]: validValue };

      setHomeTeamScorers(newScorers);
    } else {
      const newScorers = [...awayTeamScorers];
      newScorers[index] = { ...newScorers[index], [field]: validValue };

      setAwayTeamScorers(newScorers);
    }
  };

  const validateScorers = () => {
    const homeValid = homeTeamScorers.every(scorer => scorer.player_id && scorer.score_minute);
    const awayValid = awayTeamScorers.every(scorer => scorer.player_id && scorer.score_minute);

    if (!homeValid || !awayValid) {
      setScorerDialogError('Please fill in all scorer details');
      return false;
    }

    setScorerDialogError('');
    return true;
  };

  const handleScoreSubmit = async () => {
    if (!validateScorers()) return;

    try {
      const scorePayload = {
        match_id: selectedMatch.id,
        home_team_score: Number(homeTeamScore),
        away_team_score: Number(awayTeamScore),
      };

      // Update or create score
      let scoreResponse;
      if (editingScoreId) {
        scoreResponse = await fetch(`${config.apiBaseUrl}/scores/${editingScoreId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(scorePayload),
        });
      } else {
        scoreResponse = await fetch(`${config.apiBaseUrl}/scores`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(scorePayload),
        });
      }

      if (scoreResponse.ok) {
        // Delete existing scorers for this match
        await fetch(`${config.apiBaseUrl}/scorers/match/${selectedMatch.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        // Add new scorers
        const allScorers = [
          ...homeTeamScorers.map(scorer => ({
            ...scorer,
            match_id: selectedMatch.id,
          })),
          ...awayTeamScorers.map(scorer => ({
            ...scorer,
            match_id: selectedMatch.id,
          })),
        ];

        for (const scorer of allScorers) {
          await fetch(`${config.apiBaseUrl}/scorers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(scorer),
          });
        }

        setScoreDialogOpen(false);
        setSelectedMatch(null);
        setHomeTeamScore('');
        setAwayTeamScore('');
        setHomeTeamScorers([]);
        setAwayTeamScorers([]);
        setEditingScoreId(null);
        setScorerDialogError('');
        fetchMatches();
        setSnackbar({
          open: true,
          message: 'Score and scorers updated successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error updating score and scorers', severity: 'error' });
    }
  };

  const handleIncrementScore = team => {
    const event = {
      target: {
        value:
          team === 'home'
            ? String(Number(homeTeamScore || 0) + 1)
            : String(Number(awayTeamScore || 0) + 1),
      },
    };
    handleScoreChange(event, team);
  };

  // Add useEffect to log teams data when it changes
  useEffect(() => {}, [teams]);

  // Add this useEffect for debugging teams data
  useEffect(() => {
    teams.forEach(_team => {});
  }, [teams]);

  return (
    <Box sx={{ mt: { xs: 2, sm: 3, md: 4 }, p: { xs: 2, sm: 3 }, width: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            color: '#000000',
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
          }}
        >
          Matches
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setMatchDialogOpen(true)}
          sx={{
            bgcolor: '#DAA520',
            color: 'white',
            '&:hover': {
              bgcolor: '#B8860B',
            },
          }}
        >
          Add Match
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel>Filter by Team</InputLabel>
          <Select
            value={filterTeamId}
            onChange={e => setFilterTeamId(e.target.value)}
            label="Filter by Team"
          >
            <MenuItem value="">All Teams</MenuItem>
            {teams.map(team => (
              <MenuItem key={team.id} value={team.id}>
                {team.team_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={filterMatchType}
            onChange={e => setFilterMatchType(e.target.value)}
            label="Filter by Type"
          >
            <MenuItem value="">All Types</MenuItem>
            {MATCH_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Matches Grid */}
      {filteredMatches.length > 0 ? (
        <Grid container spacing={3}>
          {filteredMatches.map(match => renderMatchCard(match))}
        </Grid>
      ) : (
        <Typography sx={{ color: '#000000', textAlign: 'center' }}>
          No matches available.
        </Typography>
      )}

      {/* Add Match Dialog */}
      <Dialog
        open={matchDialogOpen}
        onClose={() => setMatchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Match</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Home Team</InputLabel>
              <Select
                value={homeTeamId}
                onChange={e => setHomeTeamId(e.target.value)}
                label="Home Team"
                required
              >
                {teams.map(team => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.team_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Away Team</InputLabel>
              <Select
                value={awayTeamId}
                onChange={e => setAwayTeamId(e.target.value)}
                label="Away Team"
                required
              >
                {teams.map(team => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.team_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Match Type</InputLabel>
              <Select
                value={matchType}
                onChange={e => setMatchType(e.target.value)}
                label="Match Type"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {MATCH_TYPES.map(t => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Match Time"
              type="datetime-local"
              value={time}
              onChange={e => setTime(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Field"
              value={field}
              onChange={e => setField(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            {formError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {formError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDialogOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button
            onClick={handleMatchSubmit}
            sx={{
              bgcolor: '#000000',
              color: '#FFD700',
              '&:hover': { bgcolor: '#333333' },
            }}
          >
            Add Match
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Match Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Match</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Home Team</InputLabel>
              <Select
                value={homeTeamId}
                onChange={e => setHomeTeamId(e.target.value)}
                label="Home Team"
                required
              >
                {teams.map(team => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.team_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Away Team</InputLabel>
              <Select
                value={awayTeamId}
                onChange={e => setAwayTeamId(e.target.value)}
                label="Away Team"
                required
              >
                {teams.map(team => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.team_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Match Type</InputLabel>
              <Select
                value={matchType}
                onChange={e => setMatchType(e.target.value)}
                label="Match Type"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {MATCH_TYPES.map(t => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Match Time"
              type="datetime-local"
              value={time}
              onChange={e => setTime(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Field"
              value={field}
              onChange={e => setField(e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={ongoing}
                  onChange={e => {
                    if (e.target.checked) {
                      setOngoing(true);
                      setIsFinished(false);
                    } else {
                      setOngoing(false);
                    }
                  }}
                  sx={{
                    color: '#DAA520',
                    '&.Mui-checked': {
                      color: '#DAA520',
                    },
                  }}
                />
              }
              label="Match Ongoing"
              sx={{ mb: 2 }}
            />

            {formError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {formError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            sx={{
              bgcolor: '#000000',
              color: '#FFD700',
              '&:hover': { bgcolor: '#333333' },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Match Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Match</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this match?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} sx={{ color: '#DAA520' }}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Score Update Dialog */}
      <Dialog
        open={scoreDialogOpen}
        onClose={() => setScoreDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Match Score</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {selectedMatch && teams.find(t => t.id === selectedMatch.home_team_id)?.team_name} vs{' '}
              {selectedMatch && teams.find(t => t.id === selectedMatch.away_team_id)?.team_name}
            </Typography>

            {/* Home Team Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedMatch && teams.find(t => t.id === selectedMatch.home_team_id)?.team_name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6">Score: {homeTeamScore || 0}</Typography>
                <IconButton
                  onClick={() => handleIncrementScore('home')}
                  sx={{
                    bgcolor: '#000000',
                    color: '#FFD700',
                    '&:hover': { bgcolor: '#333333' },
                    width: 20,
                    height: 20,
                  }}
                >
                  <span style={{ fontSize: '1.5rem' }}>+</span>
                </IconButton>
              </Box>
              {homeTeamScorers.map((scorer, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Scorer</InputLabel>
                    <Select
                      value={scorer.player_id || ''}
                      onChange={event => {
                        const selectedValue = event.target.value;

                        if (selectedValue !== undefined && selectedValue !== null) {
                          handleScorerChange(index, 'player_id', selectedValue, 'home');
                        }
                      }}
                      label="Scorer"
                    >
                      <MenuItem value="">
                        <em>Select player</em>
                      </MenuItem>
                      {selectedMatch &&
                        teams
                          .find(t => t.id === selectedMatch.home_team_id)
                          ?.players?.filter(player => player && player.id) // Filter out invalid players
                          ?.map(player => {
                            return (
                              <MenuItem key={player.id} value={String(player.id)}>
                                {player.player_name || 'Unknown Player'}
                              </MenuItem>
                            );
                          })}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Minute"
                    type="number"
                    value={scorer.score_minute || ''}
                    onChange={e =>
                      handleScorerChange(index, 'score_minute', e.target.value, 'home')
                    }
                    sx={{ width: '100px' }}
                  />
                </Box>
              ))}
            </Box>

            {/* Away Team Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedMatch && teams.find(t => t.id === selectedMatch.away_team_id)?.team_name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6">Score: {awayTeamScore || 0}</Typography>
                <IconButton
                  onClick={() => handleIncrementScore('away')}
                  sx={{
                    bgcolor: '#000000',
                    color: '#FFD700',
                    '&:hover': { bgcolor: '#333333' },
                    width: 20,
                    height: 20,
                  }}
                >
                  <span style={{ fontSize: '1.5rem' }}>+</span>
                </IconButton>
              </Box>
              {awayTeamScorers.map((scorer, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Scorer</InputLabel>
                    <Select
                      value={scorer.player_id || ''}
                      onChange={event => {
                        const selectedValue = event.target.value;

                        if (selectedValue !== undefined && selectedValue !== null) {
                          handleScorerChange(index, 'player_id', selectedValue, 'away');
                        }
                      }}
                      label="Scorer"
                    >
                      <MenuItem value="">
                        <em>Select player</em>
                      </MenuItem>
                      {selectedMatch &&
                        teams
                          .find(t => t.id === selectedMatch.away_team_id)
                          ?.players?.filter(player => player && player.id) // Filter out invalid players
                          ?.map(player => {
                            return (
                              <MenuItem key={player.id} value={String(player.id)}>
                                {player.player_name || 'Unknown Player'}
                              </MenuItem>
                            );
                          })}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Minute"
                    type="number"
                    value={scorer.score_minute || ''}
                    onChange={e =>
                      handleScorerChange(index, 'score_minute', e.target.value, 'away')
                    }
                    sx={{ width: '100px' }}
                  />
                </Box>
              ))}
            </Box>

            {scorerDialogError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {scorerDialogError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialogOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button
            onClick={handleScoreSubmit}
            sx={{
              bgcolor: '#000000',
              color: '#FFD700',
              '&:hover': { bgcolor: '#333333' },
            }}
          >
            Update Score
          </Button>
        </DialogActions>
      </Dialog>

      {/* Score Warning Dialog */}
      <Dialog
        open={scoreWarningOpen}
        onClose={() => setScoreWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#DAA520' }}>Match Not Ongoing</DialogTitle>
        <DialogContent>
          <Typography>
            You cannot update the score because this match is not marked as ongoing. Please mark the
            match as ongoing first to update its score.
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(218, 165, 32, 0.1)',
              p: 2,
              borderRadius: 1,
            }}
          >
            <InfoIcon sx={{ color: '#DAA520' }} />
            <Typography variant="body2">
              To mark a match as ongoing:
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>Click the Edit button (pencil icon)</li>
                <li>Check the "Match Ongoing" checkbox</li>
                <li>Save your changes</li>
              </ol>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setScoreWarningOpen(false);
              handleEditClick(selectedMatchForWarning);
            }}
            sx={{
              bgcolor: '#DAA520',
              color: 'white',
              '&:hover': { bgcolor: '#B8860B' },
            }}
          >
            Edit Match Status
          </Button>
          <Button onClick={() => setScoreWarningOpen(false)} sx={{ color: '#666' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminMatches;
